import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import SearchInputWithClear from 'ui/custom-controls/SearchInputWithClear';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { TaskProcessingProcessTemplate } from '../constants/TaskProcessingProcessTemplate';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import useTaskProcessingProcessService from '../hooks/services/useTaskProcessingProcessService';
import { ruLocale } from '../lang/ruLocale';
import { TaskProcessingProcess } from '../types/interfaces/TaskProcessingProcess';
import BusinessProcessesConstructor from '../ui/ProcessingWorkOrdersConstructor';
import { clearFilter } from '../utils';

export default function ProcessingWorkOrdersPage() {
  const [businessProcess, setBusinessProcess] = useState<
    TaskProcessingProcess[]
  >([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<
    string | number | null
  >(null);
  const [totalCount, setTotalCount] = useState(1);
  const [selectedBusinessProcess, setSelectedBusinessProcess] =
    useState<TaskProcessingProcess>(TaskProcessingProcessTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS
    },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [loading, setLoading] = useState<boolean>(true);
  const globalFilterRef = useRef<HTMLInputElement>(null);
  const businessProcessNameFilterRef = useRef<HTMLInputElement>(null);
  const businessProcessCardRef = useRef<null | { handleCancel: () => void }>(
    null
  );
  const descriptionFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);

  const isSelectedIdExist = isSelected(selectedBusinessProcess);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  const taskProcessingProcessService = useTaskProcessingProcessService();
  const getTaskProcessingProcessCallback = useCallback(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const globalFilter = filters.global as DataTableFilterMetaData;
    const nameFilter = filters.name as DataTableFilterMetaData;
    const descriptionFilter = filters.description as DataTableFilterMetaData;
    const serviceResult = taskProcessingProcessService.getAll({
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify([
        {
          field: 'global',
          value: globalFilter.value,
          operator: globalFilter.matchMode
        },
        {
          field: 'name',
          value: nameFilter.value,
          operator: nameFilter.matchMode
        },
        {
          field: 'description',
          value: descriptionFilter.value,
          operator: descriptionFilter.matchMode
        }
      ])
    });
    serviceResult.result.then((r) => {
      if (r) {
        setBusinessProcess(r.itemsList);
        setLoading(false);
        setTotalCount((r.total === 0 ? 1 : r.total) ?? 1);
      }
    });
    return () => serviceResult.cancel();
  }, [filters, multiSortMeta, taskProcessingProcessService]);

  useEffect(() => {
    const cancel = getTaskProcessingProcessCallback();
    return () => cancel();
  }, [getTaskProcessingProcessCallback]);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = () => {
    setSelectedBusinessProcess(TaskProcessingProcessTemplate);
    setDialogVisible(true);
  };

  const handleDeleteConfirmation = () => {
    handleDelete(employeeToDelete);
    setDeleteDialogVisible(false);
    setSelectedBusinessProcess(TaskProcessingProcessTemplate);
  };

  const handleUpdate = async (
    id: string | number,
    updatedObject: TaskProcessingProcess
  ) => {
    const result =
      await taskProcessingProcessService.addOrUpdate(updatedObject)?.result;
    getTaskProcessingProcessCallback();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await taskProcessingProcessService.delete(parseInt(id.toString()))
        ?.result;
      getTaskProcessingProcessCallback();
    }
  };

  const handleAdd = async (businessProcess: TaskProcessingProcess) => {
    const result =
      await taskProcessingProcessService.addOrUpdate(businessProcess)?.result;
    getTaskProcessingProcessCallback();
    return result;
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideBusinessProcessesConstructor = () => {
    if (businessProcessCardRef.current !== null) {
      businessProcessCardRef.current.handleCancel();
    }
  };

  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail:
          'Выберите процесс обработки наряда для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  }

  const deleteButtonHandle = () => {
    // If a valid process is selected, render the deletion confirmation dialog.
    if (isSelectedIdExist) {
      setEmployeeToDelete(selectedBusinessProcess.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите процесс обработки наряда для удаления',
        life: 3000
      });
    }
  }
  
  const header = (  
    <TablePageHeader
      tableName="processingWorkOrders"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  return (
    <div className="businessProcess-table wrapper wrapper--main">
      <Toast ref={toast} />
      <Dialog
        header="Конструктор процессов обработки нарядов"
        visible={dialogVisible}
        style={{
          minWidth: '1225px',
          width: '1225px',
          height: 'fit-content',
          maxHeight: '90vh'
        }}
        modal
        contentStyle={{ minHeight: 'fit-content', maxHeight: '70vh' }}
        onHide={() => onHideBusinessProcessesConstructor()}
      >
        <BusinessProcessesConstructor
          businessProcessProps={selectedBusinessProcess}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onAdd={handleAdd}
          closeDialog={closeDialog}
          ref={businessProcessCardRef}
        />
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="процесс обработки наряда"
        entityName={selectedBusinessProcess?.name}
      />
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedBusinessProcess!}
        onSelectionChange={(e) => setSelectedBusinessProcess(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedBusinessProcess(e.data as TaskProcessingProcess);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={businessProcess}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Процессы обработки нарядов не найдены."
        onRowReorder={(e) => setBusinessProcess(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
        footer={
          <div className="p-paginator p-component p-paginator-bottom justify-content-end" style={{
            height: 60
          }}>
            <span>{`Всего: ${businessProcess.length > 0 ? totalCount : 0}`}</span>
          </div>
        }
        pt={{
          footer: {
            style: {
              padding: 'unset',
              border: 'unset'
            }
          }
        }}
      >
        <Column
          columnKey="name"
          filter
          sortable
          field="name"
          header="Процессы обработки нарядов"
          style={{ minWidth: '1rem', width: '35%' }}
          onFilterClear={() =>
            clearFilter(
              'name',
              filters,
              setFilters,
              businessProcessNameFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={businessProcessNameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по процессам обработки нарядов"
              />
            </div>
          }
        />
        <Column
          columnKey="description"
          filter
          sortable
          field="description"
          header="Описание"
          style={{ minWidth: '1rem', width: '65%' }}
          onFilterClear={() =>
            clearFilter(
              'description',
              filters,
              setFilters,
              descriptionFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
        />
      </DataTable>
    </div>
  );
}
